import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Package2, ClipboardList, Warehouse as WarehouseIcon, Building2,
  Plus, Trash2, Upload, FileText, Loader2, Check, X, ExternalLink,
  Boxes, CircleDollarSign, ArrowUpRight, ArrowDownRight,
  Undo2, Coins, CheckCircle2, XCircle, ChevronRight, Pencil, Anchor, Globe2, Users,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import MobileListCard from '../components/MobileListCard';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { formatDate } from '../lib/dateFormat';

/**
 * AdminPallets — Pallet Ledger admin surface (Phase 1: Procurement +
 * Warehouse Inventory + per-branch ledger stub). Route: /admin/pallets
 *
 * Four tabs:
 *   1. Purchase Orders — cut new POs, list + drill-down
 *   2. GRN — receive against an existing PO; photo/doc uploads → Drive
 *   3. Warehouse Inventory — on-hand pallets by type + valuation
 *   4. Branch Ledger — per-branch balance (Phase 2 fills the transactions)
 */

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
const fmtInt = (n) => Number(n || 0).toLocaleString('en-US');
const shortDate = (iso) => formatDate(iso) || '—';

const StatusPill = ({ status }) => {
  const map = {
    Draft:    'bg-neutral-100 text-neutral-700 border-neutral-300',
    Partial:  'bg-amber-50 text-amber-800 border-amber-300',
    Received: 'bg-emerald-50 text-emerald-800 border-emerald-300',
  };
  const cls = map[status] || 'bg-neutral-100 text-neutral-600 border-neutral-300';
  return (
    <span data-testid={`pallet-po-status-${status?.toLowerCase()}`} className={`inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border ${cls}`}>
      {status || '—'}
    </span>
  );
};


// ---------------------------------------------------------------------
// Tab 1 — Purchase Orders
// ---------------------------------------------------------------------

const PurchaseOrdersTab = ({ pos, refresh, authHeaders }) => {
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    supplier_name: '',
    pallet_type: 'plastic',
    quantity: '',
    unit_price: '',
    vat_applicable: false,
    date: new Date().toISOString().slice(0, 10),
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState('');
  const [expandedGrns, setExpandedGrns] = useState({});

  const preview = useMemo(() => {
    const qty = Number(form.quantity) || 0;
    const price = Number(form.unit_price) || 0;
    const subtotal = qty * price;
    const vat = form.vat_applicable ? subtotal * 0.05 : 0;
    return { subtotal, vat, total: subtotal + vat };
  }, [form.quantity, form.unit_price, form.vat_applicable]);

  const submit = async () => {
    if (!form.supplier_name.trim()) {
      toast({ title: 'Supplier is required' });
      return;
    }
    if (!Number(form.quantity)) {
      toast({ title: 'Quantity must be > 0' });
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/admin/pallets/purchase-orders`, {
        supplier_name: form.supplier_name.trim(),
        pallet_type: form.pallet_type,
        quantity: parseInt(form.quantity, 10),
        unit_price: parseFloat(form.unit_price) || 0,
        vat_applicable: form.vat_applicable,
        date: form.date ? new Date(form.date).toISOString() : undefined,
        notes: form.notes.trim(),
      }, { headers: authHeaders });
      toast({ title: 'Purchase order created' });
      setForm({
        supplier_name: '', pallet_type: 'plastic', quantity: '', unit_price: '',
        vat_applicable: false, date: new Date().toISOString().slice(0, 10), notes: '',
      });
      setCreating(false);
      await refresh();
    } catch (err) {
      toast({ title: 'Create failed', description: err?.response?.data?.detail || err?.message });
    } finally { setBusy(false); }
  };

  const deletePO = async (po) => {
    if (!window.confirm(`Delete PO ${po.po_number}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/pallets/purchase-orders/${po.id}`, { headers: authHeaders });
      toast({ title: 'PO deleted' });
      await refresh();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err?.message });
    }
  };

  const toggleExpand = async (po) => {
    if (expanded === po.id) { setExpanded(''); return; }
    setExpanded(po.id);
    try {
      const { data } = await axios.get(`${API}/admin/pallets/purchase-orders/${po.id}`, { headers: authHeaders });
      setExpandedGrns((p) => ({ ...p, [po.id]: data.grns || [] }));
    } catch (err) {
      toast({ title: 'Load failed' });
    }
  };

  return (
    <div className="space-y-4" data-testid="pallets-po-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-neutral-900">Purchase Orders</h2>
          <p className="text-xs text-neutral-500">Cut a new PO whenever pallets are ordered. Track receiving via GRN.</p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-9"
          data-testid="pallets-po-new-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New PO
        </Button>
      </div>

      {creating && (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3" data-testid="pallets-po-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div>
              <label className="text-[10px] font-bold text-neutral-600 uppercase">Supplier</label>
              <Input value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} placeholder="Supplier name" data-testid="pallets-po-supplier" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-600 uppercase">Pallet type</label>
              <select
                value={form.pallet_type}
                onChange={(e) => setForm({ ...form, pallet_type: e.target.value })}
                className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
                data-testid="pallets-po-type"
              >
                <option value="plastic">Plastic</option>
                <option value="wooden">Wooden</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-600 uppercase">Date</label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="pallets-po-date" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-600 uppercase">Quantity</label>
              <Input type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} placeholder="e.g. 100" data-testid="pallets-po-qty" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-600 uppercase">Unit price (OMR)</label>
              <Input type="number" min="0" step="0.001" value={form.unit_price} onChange={(e) => setForm({ ...form, unit_price: e.target.value })} placeholder="0.000" data-testid="pallets-po-price" />
            </div>
            <div className="flex flex-col justify-end">
              <label className="text-[10px] font-bold text-neutral-600 uppercase mb-1">VAT (5%)</label>
              <label className="inline-flex items-center gap-2 cursor-pointer h-10">
                <input
                  type="checkbox"
                  checked={form.vat_applicable}
                  onChange={(e) => setForm({ ...form, vat_applicable: e.target.checked })}
                  className="w-4 h-4"
                  data-testid="pallets-po-vat"
                />
                <span className="text-sm text-neutral-700">{form.vat_applicable ? 'Applicable' : 'Exempt'}</span>
              </label>
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold text-neutral-600 uppercase">Notes (optional)</label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Payment terms, colour, condition..." data-testid="pallets-po-notes" />
          </div>
          <div className="flex items-center justify-between border-t border-indigo-200 pt-3">
            <div className="flex gap-6 text-xs" data-testid="pallets-po-preview">
              <div><span className="text-neutral-500">Subtotal:</span> <b className="text-neutral-900">OMR {fmt(preview.subtotal)}</b></div>
              <div><span className="text-neutral-500">VAT:</span> <b className="text-neutral-900">OMR {fmt(preview.vat)}</b></div>
              <div className="text-base"><span className="text-neutral-500">Total:</span> <b className="text-indigo-700">OMR {fmt(preview.total)}</b></div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCreating(false)} className="rounded-full h-9" data-testid="pallets-po-cancel">Cancel</Button>
              <Button onClick={submit} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-9" data-testid="pallets-po-save">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Check className="w-4 h-4 mr-1.5" />}
                Save PO
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile PO card list — Feb 2026 native-app pass */}
      <div className="md:hidden space-y-2" data-testid="pallets-po-list-mobile">
        {pos.length === 0 && (
          <div className="p-6 text-center text-neutral-500 italic text-sm">
            No purchase orders yet. Cut your first PO with the button above.
          </div>
        )}
        {pos.map((po) => (
          <MobileListCard
            key={po.id}
            testId={`pallets-po-card-${po.id}`}
            title={
              <div className="flex items-center gap-2">
                <span className="font-mono text-[13px]">{po.po_number}</span>
                <span className="text-[11px] font-semibold text-neutral-500 capitalize">
                  · {po.pallet_type}
                </span>
              </div>
            }
            statusPill={<StatusPill status={po.status} />}
            businessName={po.supplier_name}
            customerName={shortDate(po.date)}
            amount={fmt(po.total)}
            date={`${fmtInt(po.received_qty)} / ${fmtInt(po.quantity)} received`}
            onClick={() => toggleExpand(po)}
            actions={[
              { label: 'View GRNs', onClick: () => toggleExpand(po), testId: `pallets-po-card-view-${po.id}` },
              ...(po.received_qty === 0
                ? [{ divider: true }, { label: 'Delete draft', onClick: () => deletePO(po), danger: true, testId: `pallets-po-card-delete-${po.id}` }]
                : []),
            ]}
          />
        ))}
      </div>

      <div className="hidden md:block rounded-2xl border border-neutral-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
              <th className="p-3">PO #</th>
              <th className="p-3">Date</th>
              <th className="p-3">Supplier</th>
              <th className="p-3">Type</th>
              <th className="p-3 text-right">Qty</th>
              <th className="p-3 text-right">Received</th>
              <th className="p-3 text-right">Unit Price</th>
              <th className="p-3 text-right">Total (VAT)</th>
              <th className="p-3">Status</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {pos.length === 0 && (
              <tr><td colSpan={10} className="p-8 text-center text-neutral-500 italic" data-testid="pallets-po-empty">No purchase orders yet. Cut your first PO with the button above.</td></tr>
            )}
            {pos.map((po) => (
              <React.Fragment key={po.id}>
                <tr data-testid={`pallets-po-row-${po.id}`} className="hover:bg-neutral-50">
                  <td className="p-3 font-mono text-xs">{po.po_number}</td>
                  <td className="p-3">{shortDate(po.date)}</td>
                  <td className="p-3">{po.supplier_name}</td>
                  <td className="p-3 capitalize">{po.pallet_type}</td>
                  <td className="p-3 text-right">{fmtInt(po.quantity)}</td>
                  <td className="p-3 text-right">{fmtInt(po.received_qty)} <span className="text-neutral-400">/{fmtInt(po.quantity)}</span></td>
                  <td className="p-3 text-right">{fmt(po.unit_price)}</td>
                  <td className="p-3 text-right font-semibold">{fmt(po.total)}</td>
                  <td className="p-3"><StatusPill status={po.status} /></td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <Button size="sm" variant="outline" onClick={() => toggleExpand(po)} title="View GRNs" data-testid={`pallets-po-expand-${po.id}`}>
                        <FileText className="w-3.5 h-3.5" />
                      </Button>
                      {po.received_qty === 0 && (
                        <Button size="sm" variant="ghost" onClick={() => deletePO(po)} className="text-red-600" title="Delete draft" data-testid={`pallets-po-delete-${po.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
                {expanded === po.id && (
                  <tr>
                    <td colSpan={10} className="bg-slate-50 p-3">
                      <div className="text-[10px] uppercase tracking-wide font-bold text-neutral-500 mb-2">GRNs against this PO</div>
                      {(expandedGrns[po.id] || []).length === 0 ? (
                        <div className="text-xs italic text-neutral-500">No goods receipts recorded yet.</div>
                      ) : (
                        <div className="space-y-1.5">
                          {(expandedGrns[po.id] || []).map((g) => (
                            <div key={g.id} className="flex items-center justify-between rounded-lg border border-neutral-200 bg-white p-2 text-xs" data-testid={`pallets-po-grn-${g.id}`}>
                              <span className="font-mono">{g.grn_number}</span>
                              <span>{shortDate(g.receive_date)}</span>
                              <span><b>{fmtInt(g.received_qty)}</b> pallets received</span>
                              <span className="text-neutral-500 truncate max-w-[240px]">{g.condition_notes || '—'}</span>
                              <span className="text-neutral-500">{g.documents?.length || 0} attachment{g.documents?.length === 1 ? '' : 's'}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------
// Tab 2 — GRN
// ---------------------------------------------------------------------

const GRNTab = ({ pos, grns, refresh, authHeaders }) => {
  const { toast } = useToast();
  const [creating, setCreating] = useState(false);
  const [selectedPoId, setSelectedPoId] = useState('');
  const [form, setForm] = useState({ received_qty: '', receive_date: new Date().toISOString().slice(0, 10), condition_notes: '' });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const eligiblePOs = pos.filter((p) => p.remaining_qty > 0);
  const selectedPO = pos.find((p) => p.id === selectedPoId);

  const submit = async () => {
    if (!selectedPO) { toast({ title: 'Pick a PO first' }); return; }
    const qty = parseInt(form.received_qty, 10);
    if (!qty || qty <= 0) { toast({ title: 'Received qty must be > 0' }); return; }
    if (qty > selectedPO.remaining_qty) {
      toast({ title: `Only ${selectedPO.remaining_qty} pallets remain on this PO` });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('po_id', selectedPO.id);
      fd.append('received_qty', String(qty));
      if (form.receive_date) fd.append('receive_date', new Date(form.receive_date).toISOString());
      if (form.condition_notes.trim()) fd.append('condition_notes', form.condition_notes.trim());
      (files || []).forEach((f) => fd.append('files', f));
      await axios.post(`${API}/admin/pallets/grns`, fd, {
        headers: { ...authHeaders, 'Content-Type': 'multipart/form-data' },
      });
      toast({ title: 'GRN saved · warehouse inventory updated' });
      setForm({ received_qty: '', receive_date: new Date().toISOString().slice(0, 10), condition_notes: '' });
      setSelectedPoId('');
      setFiles([]);
      setCreating(false);
      await refresh();
    } catch (err) {
      toast({ title: 'GRN save failed', description: err?.response?.data?.detail || err?.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="pallets-grn-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-neutral-900">Goods Received Notes</h2>
          <p className="text-xs text-neutral-500">Record physical receipts against an open PO. Photos + docs sync to Google Drive.</p>
        </div>
        <Button
          onClick={() => setCreating(true)}
          disabled={eligiblePOs.length === 0}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-9"
          data-testid="pallets-grn-new-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" /> New GRN
        </Button>
      </div>
      {eligiblePOs.length === 0 && !creating && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          No POs with remaining qty. Create a PO in the Purchase Orders tab before recording a GRN.
        </div>
      )}

      {creating && (
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3" data-testid="pallets-grn-form">
          <div>
            <label className="text-[10px] font-bold text-neutral-600 uppercase">Select PO</label>
            <select
              value={selectedPoId}
              onChange={(e) => setSelectedPoId(e.target.value)}
              className="h-10 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
              data-testid="pallets-grn-po-select"
            >
              <option value="">— Select a PO —</option>
              {eligiblePOs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.po_number} · {p.supplier_name} · {p.pallet_type} · {p.remaining_qty} remaining
                </option>
              ))}
            </select>
          </div>
          {selectedPO && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs bg-white rounded-xl p-3 border border-neutral-200" data-testid="pallets-grn-po-summary">
              <div><span className="text-neutral-500">Pallet type</span><br /><b className="capitalize">{selectedPO.pallet_type}</b></div>
              <div><span className="text-neutral-500">Unit price</span><br /><b>OMR {fmt(selectedPO.unit_price)}</b></div>
              <div><span className="text-neutral-500">Ordered</span><br /><b>{fmtInt(selectedPO.quantity)}</b></div>
              <div><span className="text-neutral-500">Remaining</span><br /><b className="text-emerald-700">{fmtInt(selectedPO.remaining_qty)}</b></div>
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold text-neutral-600 uppercase">Received qty</label>
              <Input type="number" min="1" value={form.received_qty} onChange={(e) => setForm({ ...form, received_qty: e.target.value })} placeholder={selectedPO ? `Max ${selectedPO.remaining_qty}` : ''} data-testid="pallets-grn-qty" />
            </div>
            <div>
              <label className="text-[10px] font-bold text-neutral-600 uppercase">Receive date</label>
              <Input type="date" value={form.receive_date} onChange={(e) => setForm({ ...form, receive_date: e.target.value })} data-testid="pallets-grn-date" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold text-neutral-600 uppercase">Condition notes</label>
            <Input value={form.condition_notes} onChange={(e) => setForm({ ...form, condition_notes: e.target.value })} placeholder="Any damage, shortages, colour mismatch..." data-testid="pallets-grn-notes" />
          </div>
          <div>
            <label className="text-[10px] font-bold text-neutral-600 uppercase mb-1 block">Documents / photos (upload to Google Drive)</label>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              data-testid="pallets-grn-files"
              className="text-xs"
            />
            {files.length > 0 && (
              <div className="mt-1 text-xs text-neutral-600" data-testid="pallets-grn-file-count">{files.length} file{files.length === 1 ? '' : 's'} ready</div>
            )}
          </div>
          <div className="flex justify-end gap-2 border-t border-indigo-200 pt-3">
            <Button variant="outline" onClick={() => { setCreating(false); setSelectedPoId(''); setFiles([]); }} className="rounded-full h-9" data-testid="pallets-grn-cancel">Cancel</Button>
            <Button onClick={submit} disabled={busy || !selectedPO} className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full h-9" data-testid="pallets-grn-save">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Upload className="w-4 h-4 mr-1.5" />}
              Save GRN
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-neutral-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
              <th className="p-3">GRN #</th>
              <th className="p-3">PO #</th>
              <th className="p-3">Received</th>
              <th className="p-3">Type</th>
              <th className="p-3 text-right">Qty</th>
              <th className="p-3">Notes</th>
              <th className="p-3">Attachments</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {grns.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-neutral-500 italic" data-testid="pallets-grn-empty">No GRNs recorded yet.</td></tr>
            )}
            {grns.map((g) => (
              <tr key={g.id} data-testid={`pallets-grn-row-${g.id}`} className="hover:bg-neutral-50">
                <td className="p-3 font-mono text-xs">{g.grn_number}</td>
                <td className="p-3 font-mono text-xs text-neutral-600">{g.po_number}</td>
                <td className="p-3">{shortDate(g.receive_date)}</td>
                <td className="p-3 capitalize">{g.pallet_type}</td>
                <td className="p-3 text-right font-semibold">{fmtInt(g.received_qty)}</td>
                <td className="p-3 text-xs text-neutral-600 max-w-[280px] truncate">{g.condition_notes || '—'}</td>
                <td className="p-3">
                  {(g.documents || []).length === 0 ? <span className="text-xs text-neutral-400">—</span> : (
                    <div className="flex gap-1 flex-wrap">
                      {g.documents.map((d) => (
                        <a
                          key={d.file_id}
                          href={d.web_view_link || `${API}/admin/pallets/documents/${d.file_id}`}
                          target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-200 px-2 py-0.5 text-[11px] text-indigo-700 hover:bg-indigo-100"
                          data-testid={`pallets-grn-doc-${g.id}-${d.file_id}`}
                        >
                          <ExternalLink className="w-3 h-3" /> {d.name}
                        </a>
                      ))}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------
// Tab 3 — Warehouse Inventory
// ---------------------------------------------------------------------

const InventoryTab = ({ inventory, transactions, isSuper, authHeaders, refresh }) => {
  const { toast } = useToast();
  const [busyId, setBusyId] = useState('');

  const deleteTx = async (tx) => {
    if (!window.confirm(
      'Delete this ledger entry?\n\nRunning balance and total liability will be recalculated. This cannot be undone.'
    )) return;
    setBusyId(tx.id);
    try {
      await axios.delete(
        `${API}/admin/pallets/transactions/${tx.id}`,
        { headers: authHeaders },
      );
      toast({ title: 'Transaction deleted' });
      await refresh?.();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setBusyId('');
    }
  };

  return (
    <div className="space-y-4" data-testid="pallets-inventory-tab">
      <div>
        <h2 className="text-lg font-bold text-neutral-900">Warehouse Pallet Inventory</h2>
        <p className="text-xs text-neutral-500">Live count updated on every GRN. Phase 2 will also debit for dispatched pallets and credit physical branch returns.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {inventory?.by_type?.map((row) => (
          <div key={row.pallet_type} className="rounded-2xl border border-neutral-200 bg-white p-4" data-testid={`pallets-inventory-tile-${row.pallet_type}`}>
            <div className="flex items-center gap-2 text-neutral-500 text-xs uppercase tracking-wide">
              <Boxes className="w-4 h-4" /> {row.pallet_type} pallets
            </div>
            <div className="text-3xl font-bold text-neutral-900 mt-1" data-testid={`pallets-inventory-qty-${row.pallet_type}`}>{fmtInt(row.qty)}</div>
            <div className="text-xs text-neutral-500 mt-1">
              Value: <b className="text-neutral-800">OMR {fmt(row.amount)}</b>
            </div>
            <div className="text-[10px] text-neutral-400 mt-0.5">
              Latest unit price: OMR {fmt(row.latest_unit_price)}
            </div>
          </div>
        ))}
        <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4" data-testid="pallets-inventory-tile-total">
          <div className="flex items-center gap-2 text-indigo-800 text-xs uppercase tracking-wide">
            <CircleDollarSign className="w-4 h-4" /> Total on hand
          </div>
          <div className="text-3xl font-bold text-indigo-900 mt-1" data-testid="pallets-inventory-total-qty">{fmtInt(inventory?.total_qty)}</div>
          <div className="text-xs text-indigo-700 mt-1">Total value: <b>OMR {fmt(inventory?.total_amount)}</b></div>
        </div>
      </div>

      <div className="rounded-2xl border border-neutral-200 overflow-x-auto">
        <div className="bg-neutral-50 border-b border-neutral-200 p-3 text-xs font-bold uppercase tracking-wider text-neutral-600">
          Recent transactions
        </div>
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50/70 border-b border-neutral-100 text-[10px] uppercase text-neutral-500 font-bold">
            <tr>
              <th className="p-2 text-left">When</th>
              <th className="p-2 text-left">Kind</th>
              <th className="p-2 text-left">Type</th>
              <th className="p-2 text-right">Qty</th>
              <th className="p-2 text-right">Unit Price</th>
              <th className="p-2 text-right">Total</th>
              <th className="p-2 text-left">Ref</th>
              <th className="p-2 text-left">By</th>
              {isSuper && <th className="p-2 text-right w-10">·</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {(transactions || []).length === 0 && (
              <tr><td colSpan={isSuper ? 9 : 8} className="p-6 text-center italic text-neutral-500" data-testid="pallets-tx-empty">No transactions yet.</td></tr>
            )}
            {(transactions || []).map((tx) => {
              const absQty = Math.abs(tx.qty || 0);
              const unitPrice = Number(tx.unit_price) > 0
                ? Number(tx.unit_price)
                : (absQty > 0 ? Math.abs(Number(tx.amount || 0)) / absQty : 0);
              return (
              <tr key={tx.id} data-testid={`pallets-tx-row-${tx.id}`}>
                <td className="p-2 text-xs">{shortDate(tx.ts)}</td>
                <td className="p-2 text-xs capitalize">{(tx.kind || '').replaceAll('_', ' ')}</td>
                <td className="p-2 text-xs capitalize">{tx.pallet_type}</td>
                <td className={`p-2 text-right font-semibold ${tx.qty >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>
                  {tx.qty >= 0 ? <ArrowUpRight className="w-3.5 h-3.5 inline-block" /> : <ArrowDownRight className="w-3.5 h-3.5 inline-block" />}
                  {fmtInt(absQty)}
                </td>
                <td className="p-2 text-right font-mono text-xs" data-testid={`pallets-tx-unit-price-${tx.id}`}>{fmt(unitPrice)}</td>
                <td className="p-2 text-right font-mono text-xs" data-testid={`pallets-tx-total-${tx.id}`}>{fmt(tx.amount)}</td>
                <td className="p-2 text-xs font-mono text-neutral-600">
                  {tx.grn_number || tx.po_number || '—'}
                </td>
                <td className="p-2 text-xs text-neutral-500 truncate max-w-[140px]">{tx.created_by || '—'}</td>
                {isSuper && (
                  <td className="p-2 text-right">
                    <button
                      type="button"
                      onClick={() => deleteTx(tx)}
                      disabled={busyId === tx.id}
                      title="Permanently delete this ledger entry"
                      className="inline-flex items-center rounded-full border border-red-300 bg-white text-red-600 hover:bg-red-50 hover:border-red-500 text-[10px] p-1 disabled:opacity-50"
                      data-testid={`pallets-tx-delete-btn-${tx.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------
// Tab 4 — Branch Ledger (Phase 2: real per-branch balances + drill-down)
// ---------------------------------------------------------------------

const BranchLedgerTab = ({ ledger, authHeaders, refresh, isSuper }) => {
  const { toast } = useToast();
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmingId, setConfirmingId] = useState('');
  const [openingForm, setOpeningForm] = useState(null); // { qty, amount, notes } | null
  const [savingOpening, setSavingOpening] = useState(false);

  const openDrill = async (branch) => {
    setSelected(branch);
    setDetailLoading(true);
    try {
      const isExport = branch.party_kind === 'export_customer';
      const isLocal = branch.party_kind === 'local_customer';
      let url;
      if (isExport) {
        url = `${API}/admin/pallets/ledger/export/${encodeURIComponent(branch.export_customer_id || branch.email)}`;
      } else if (isLocal) {
        url = `${API}/admin/pallets/ledger/local/${encodeURIComponent(branch.local_customer_id || branch.customer_number)}`;
      } else {
        url = `${API}/admin/pallets/ledger/${branch.branch_id}`;
      }
      const { data } = await axios.get(url, { headers: authHeaders });
      setDetail(data);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || err?.message });
    } finally { setDetailLoading(false); }
  };

  // Feb 2026 — Super-admin only. Permanently deletes a
  // `pallet_transactions` row. Running balance + monetary liability
  // recompute from the aggregation on the next fetch, so we just
  // re-open the drill to hydrate the fresh totals + row list.
  const [deletingTxId, setDeletingTxId] = useState('');
  const deleteTx = async (tx) => {
    if (tx.kind === 'opening_balance' || String(tx.id || '').startsWith('opening-')) {
      toast({ title: 'Cannot delete opening balance', description: 'Use the Edit opening balance section above instead.' });
      return;
    }
    if (!window.confirm(
      'Delete this ledger entry?\n\nRunning balance and total liability will be recalculated. This cannot be undone.'
    )) return;
    setDeletingTxId(tx.id);
    try {
      await axios.delete(
        `${API}/admin/pallets/transactions/${tx.id}`,
        { headers: authHeaders },
      );
      toast({ title: 'Ledger entry deleted' });
      // Refresh both the summary ledger AND the drill detail.
      if (selected) {
        await openDrill(selected);
      }
      await refresh?.();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setDeletingTxId('');
    }
  };

  const saveOpeningBalance = async () => {
    if (!openingForm || !selected) return;
    const parseNum = (raw, fallback = 0) => {
      const n = Number(raw);
      return Number.isFinite(n) && n >= 0 ? n : fallback;
    };
    const plasticQty = parseInt(openingForm.plastic_qty, 10);
    const plasticAmount = parseFloat(openingForm.plastic_amount);
    const woodenQty = parseInt(openingForm.wooden_qty, 10);
    const woodenAmount = parseFloat(openingForm.wooden_amount);
    if ([plasticQty, plasticAmount, woodenQty, woodenAmount].some((v) => Number.isNaN(v) || v < 0)) {
      toast({ title: 'All qty + amount fields must be ≥ 0' });
      return;
    }
    setSavingOpening(true);
    try {
      const notes = (openingForm.notes || '').trim();
      const isExport = selected.party_kind === 'export_customer';
      const isLocal = selected.party_kind === 'local_customer';
      let url;
      if (isExport) {
        url = `${API}/admin/pallets/opening-balance/export/${encodeURIComponent(selected.export_customer_id || selected.email)}`;
      } else if (isLocal) {
        url = `${API}/admin/pallets/opening-balance/local/${encodeURIComponent(selected.local_customer_id || selected.customer_number)}`;
      } else {
        url = `${API}/admin/pallets/opening-balance/${selected.branch_id}`;
      }
      await axios.put(
        url,
        {
          plastic: {
            opening_qty: parseNum(plasticQty),
            opening_amount: parseNum(plasticAmount),
            notes,
          },
          wooden: {
            opening_qty: parseNum(woodenQty),
            opening_amount: parseNum(woodenAmount),
            notes,
          },
        },
        { headers: authHeaders },
      );
      toast({ title: 'Opening balance saved' });
      setOpeningForm(null);
      await openDrill(selected);
      await refresh();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err?.message });
    } finally { setSavingOpening(false); }
  };

  const confirmReturn = async (returnId) => {
    setConfirmingId(returnId);
    try {
      await axios.post(`${API}/admin/pallets/returns/${returnId}/confirm`, {}, { headers: authHeaders });
      toast({ title: 'Return confirmed' });
      await openDrill(selected);
      await refresh();
    } catch (err) {
      toast({ title: 'Confirm failed', description: err?.response?.data?.detail || err?.message });
    } finally { setConfirmingId(''); }
  };

  const rejectReturn = async (returnId) => {
    if (!window.confirm('Reject this return? The branch will need to resubmit.')) return;
    setConfirmingId(returnId);
    try {
      await axios.post(`${API}/admin/pallets/returns/${returnId}/reject`, {}, { headers: authHeaders });
      toast({ title: 'Return rejected' });
      await openDrill(selected);
      await refresh();
    } catch (err) {
      toast({ title: 'Reject failed', description: err?.response?.data?.detail || err?.message });
    } finally { setConfirmingId(''); }
  };

  return (
    <div className="space-y-4" data-testid="pallets-ledger-tab">
      {/* Feb 2026 — Three separate ledger sections per user request:
          1. Branches Pallet Ledger
          2. Local Customer Pallet Ledger
          3. Export Customer Pallet Ledger
          Each is a standalone table so ops can scan the three
          audiences (internal branches, direct local customers,
          international export customers) without them mixing. */}
      <div>
        <h2 className="text-lg font-bold text-neutral-900 inline-flex items-center gap-2">
          <Building2 className="w-4 h-4 text-indigo-600" /> Branch Pallet Ledger
        </h2>
        <p className="text-xs text-neutral-500">Positive balances = branch still holding our pallets. Click a row to drill down, set opening balance, or reconcile returns.</p>
      </div>
      <div className="rounded-2xl border border-neutral-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
              <th className="p-3">Branch</th>
              <th className="p-3">Username</th>
              <th className="p-3 text-right">Pallet balance</th>
              <th className="p-3 text-right">Monetary liability</th>
              <th className="p-3 text-right">Pending</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {(ledger?.branches || []).length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center italic text-neutral-500" data-testid="pallets-ledger-empty">No branches configured.</td></tr>
            )}
            {(ledger?.branches || []).map((b) => (
              <tr
                key={b.branch_id}
                data-testid={`pallets-ledger-row-${b.branch_id}`}
                onClick={() => openDrill({ ...b, party_kind: 'branch' })}
                className="hover:bg-indigo-50 cursor-pointer"
              >
                <td className="p-3 font-semibold inline-flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-indigo-600 shrink-0" />
                  <span>{b.branch_name}</span>
                </td>
                <td className="p-3 text-xs text-neutral-500 font-mono">@{b.username}</td>
                <td className="p-3 text-right"><b className={b.pallet_balance > 0 ? 'text-red-600' : 'text-neutral-500'}>{fmtInt(b.pallet_balance)}</b></td>
                <td className="p-3 text-right"><b className={b.monetary_liability > 0 ? 'text-red-600' : 'text-neutral-500'}>OMR {fmt(b.monetary_liability)}</b></td>
                <td className="p-3 text-right">
                  {b.pending_returns > 0 ? (
                    <span className="inline-block bg-amber-100 text-amber-800 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full" data-testid={`pallets-ledger-pending-${b.branch_id}`}>{b.pending_returns} pending</span>
                  ) : <span className="text-neutral-400 text-xs">—</span>}
                </td>
                <td className="p-3 text-right"><ChevronRight className="w-4 h-4 text-neutral-400 inline-block" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Feb 2026 — Local Customer Pallet Ledger. Aggregated from
          order_dispatch txs stamped with `local_customer_id`. Read-only
          for now (no opening balance yet). */}
      <div>
        <h2 className="text-lg font-bold text-neutral-900 mt-6 inline-flex items-center gap-2">
          <Users className="w-4 h-4 text-sky-700" /> Local Customer Pallet Ledger
        </h2>
        <p className="text-xs text-neutral-500">Pallet balances for direct (non-branch, non-export) customer orders. Click a row to drill down.</p>
      </div>
      <div className="rounded-2xl border border-neutral-200 overflow-x-auto" data-testid="pallets-ledger-local-table">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
              <th className="p-3">Customer</th>
              <th className="p-3">Customer #</th>
              <th className="p-3 text-right">Pallet balance</th>
              <th className="p-3 text-right">Monetary liability</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {(ledger?.local_customers || []).length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center italic text-neutral-500" data-testid="pallets-ledger-local-empty">No local customers with pallet activity yet.</td></tr>
            )}
            {(ledger?.local_customers || []).map((c) => (
              <tr
                key={c.local_customer_id}
                data-testid={`pallets-ledger-local-row-${c.local_customer_id}`}
                onClick={() => openDrill({ ...c, party_kind: 'local_customer' })}
                className="hover:bg-sky-50 cursor-pointer"
              >
                <td className="p-3">
                  <div className="font-semibold">{c.name}</div>
                  {c.contact_name && c.contact_name !== c.name && (
                    <div className="text-[11px] text-neutral-500">{c.contact_name}</div>
                  )}
                  {c.phone && <div className="text-[11px] text-neutral-500 font-mono">{c.phone}</div>}
                </td>
                <td className="p-3 text-xs font-mono">{c.customer_number}</td>
                <td className="p-3 text-right"><b className={c.pallet_balance > 0 ? 'text-red-600' : 'text-neutral-500'}>{fmtInt(c.pallet_balance)}</b></td>
                <td className="p-3 text-right"><b className={c.monetary_liability > 0 ? 'text-red-600' : 'text-neutral-500'}>OMR {fmt(c.monetary_liability)}</b></td>
                <td className="p-3 text-right"><ChevronRight className="w-4 h-4 text-neutral-400 inline-block" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Feb 2026 — Export Customer Pallet Ledger. Standalone section
          (previously merged with branches — un-merged per user
          request for three separate sections). Country chip + master
          registry (`EXP-XXXXXX`) surfaced for context. */}
      <div>
        <h2 className="text-lg font-bold text-neutral-900 mt-6 inline-flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-amber-700" /> Export Customer Pallet Ledger
        </h2>
        <p className="text-xs text-neutral-500">Pallet balances for international export orders. Click a row to drill down, set opening balance, or reconcile returns.</p>
      </div>
      <div className="rounded-2xl border border-neutral-200 overflow-x-auto" data-testid="pallets-ledger-export-table">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
              <th className="p-3">Customer</th>
              <th className="p-3">Country</th>
              <th className="p-3 text-right">Pallet balance</th>
              <th className="p-3 text-right">Monetary liability</th>
              <th className="p-3 text-right">Pending</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {(ledger?.export_customers || []).length === 0 && (
              <tr><td colSpan={6} className="p-6 text-center italic text-neutral-500" data-testid="pallets-ledger-export-empty">No export customers configured.</td></tr>
            )}
            {(ledger?.export_customers || []).map((c) => (
              <tr
                key={c.export_customer_id}
                data-testid={`pallets-ledger-export-row-${c.export_customer_id}`}
                onClick={() => openDrill({ ...c, party_kind: 'export_customer' })}
                className="hover:bg-amber-50 cursor-pointer"
              >
                <td className="p-3">
                  <div className="font-semibold inline-flex items-center gap-2">
                    <Globe2 className="w-4 h-4 text-amber-700 shrink-0" />
                    <span>{c.name}</span>
                  </div>
                  <div className="text-[11px] text-neutral-500 font-mono mt-0.5">
                    {c.customer_number ? `${c.customer_number} · ${c.email}` : c.email}
                  </div>
                </td>
                <td className="p-3 text-xs">
                  {c.country_name ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold">
                      {c.country_name}{c.country_code ? ` (${c.country_code})` : ''}
                    </span>
                  ) : <span className="text-neutral-400">—</span>}
                </td>
                <td className="p-3 text-right"><b className={c.pallet_balance > 0 ? 'text-red-600' : 'text-neutral-500'}>{fmtInt(c.pallet_balance)}</b></td>
                <td className="p-3 text-right"><b className={c.monetary_liability > 0 ? 'text-red-600' : 'text-neutral-500'}>OMR {fmt(c.monetary_liability)}</b></td>
                <td className="p-3 text-right">
                  {c.pending_returns > 0 ? (
                    <span className="inline-block bg-amber-100 text-amber-800 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full">{c.pending_returns} pending</span>
                  ) : <span className="text-neutral-400 text-xs">—</span>}
                </td>
                <td className="p-3 text-right"><ChevronRight className="w-4 h-4 text-neutral-400 inline-block" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" onClick={() => { setSelected(null); setDetail(null); }}>
          <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="pallets-ledger-drill">
            <div className="px-5 py-4 border-b border-neutral-200 flex items-center justify-between">
              <div>
                <div className="text-lg font-bold text-neutral-900 inline-flex items-center gap-2">
                  {selected.party_kind === 'export_customer' && <Globe2 className="w-4 h-4 text-amber-700" />}
                  {selected.party_kind === 'local_customer' && <Users className="w-4 h-4 text-sky-700" />}
                  {selected.branch_name || selected.name || selected.email}
                </div>
                <div className="text-xs text-neutral-500">
                  {selected.party_kind === 'export_customer'
                    ? `Export customer · ${selected.country_name || selected.country_code || 'international'} · ${selected.email || ''}`
                    : selected.party_kind === 'local_customer'
                      ? `Local customer · # ${selected.customer_number || selected.local_customer_id || ''}`
                      : 'Pallet ledger drill-down'}
                </div>
              </div>
              <button onClick={() => { setSelected(null); setDetail(null); }} className="text-neutral-400 hover:text-neutral-800" data-testid="pallets-ledger-drill-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 overflow-y-auto flex-1">
              {detailLoading ? (
                <div className="text-center py-8 text-neutral-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className={`rounded-xl border p-4 ${(detail?.pallet_balance ?? selected.pallet_balance) > 0 ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="pallets-drill-balance">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-neutral-600 flex items-center gap-1.5">
                        <Boxes className="w-3.5 h-3.5" /> Pallet balance
                      </div>
                      <div className={`text-3xl font-bold mt-1 tabular-nums ${(detail?.pallet_balance ?? selected.pallet_balance) > 0 ? 'text-amber-900' : 'text-emerald-700'}`} data-testid="pallets-drill-balance-qty">{fmtInt(detail?.pallet_balance ?? selected.pallet_balance)}</div>
                      <div className="text-[10px] mt-0.5 text-neutral-500">Total pallets currently owed by branch.</div>
                    </div>
                    <div className={`rounded-xl border p-4 ${(detail?.monetary_liability ?? selected.monetary_liability) > 0 ? 'bg-red-50 border-red-200' : 'bg-emerald-50 border-emerald-200'}`} data-testid="pallets-drill-liability">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-neutral-600 flex items-center gap-1.5">
                        <CircleDollarSign className="w-3.5 h-3.5" /> Total liability
                      </div>
                      <div className={`text-3xl font-bold mt-1 tabular-nums ${(detail?.monetary_liability ?? selected.monetary_liability) > 0 ? 'text-red-700' : 'text-emerald-700'}`} data-testid="pallets-drill-liability-amount">OMR {fmt(detail?.monetary_liability ?? selected.monetary_liability)}</div>
                      <div className="text-[10px] mt-0.5 text-neutral-500">Sum of all pallet transactions plus opening balances.</div>
                    </div>
                  </div>

                  {/* Opening Balance section — TWO entries per party
                      (branch / local customer / export customer),
                      one per pallet type (plastic + wooden). Totals
                      shown at the top of the drill-down modal already
                      include both, but each type gets its own row +
                      inputs so ops can carry forward the exact figure
                      from the previous paper ledger per type. */}
                  <div className="rounded-xl bg-sky-50 border border-sky-200 p-3 mb-4" data-testid="pallets-drill-opening">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Anchor className="w-4 h-4 text-sky-700" />
                        <span className="text-xs font-bold uppercase tracking-wide text-sky-900">Opening balance</span>
                        {!isSuper && (
                          <span
                            className="text-[10px] font-bold uppercase tracking-wide bg-neutral-100 text-neutral-600 border border-neutral-300 rounded-full px-2 py-0.5"
                            title="Only the super admin can add or edit opening balances"
                            data-testid="pallets-drill-opening-super-only-badge"
                          >
                            Super-admin only
                          </span>
                        )}
                      </div>
                      {isSuper && (
                        <button
                          type="button"
                          onClick={() => setOpeningForm({
                            plastic_qty: String(detail?.opening_plastic?.opening_qty ?? 0),
                            plastic_amount: String(detail?.opening_plastic?.opening_unit_price ?? 0),
                            wooden_qty: String(detail?.opening_wooden?.opening_qty ?? 0),
                            wooden_amount: String(detail?.opening_wooden?.opening_unit_price ?? 0),
                            notes: detail?.opening_plastic?.notes || detail?.opening_wooden?.notes || '',
                          })}
                          className="text-xs px-2.5 py-1 rounded-full bg-white border border-sky-300 text-sky-800 hover:bg-sky-100 inline-flex items-center gap-1"
                          data-testid="pallets-drill-opening-edit"
                        >
                          <Pencil className="w-3 h-3" />
                          {(detail?.opening_qty || detail?.opening_amount) ? 'Edit' : 'Set'}
                        </button>
                      )}
                    </div>
                    {openingForm && isSuper ? (
                      <div className="space-y-3" data-testid="pallets-drill-opening-form">
                        <div className="rounded-lg bg-white/70 border border-sky-100 p-2">
                          <div className="text-[10px] font-bold text-sky-900 uppercase tracking-wide mb-1">Plastic pallets</div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] font-bold text-neutral-600 uppercase">Qty</label>
                              <Input
                                type="number"
                                min="0"
                                value={openingForm.plastic_qty}
                                onChange={(e) => setOpeningForm({ ...openingForm, plastic_qty: e.target.value })}
                                placeholder="0"
                                data-testid="pallets-drill-opening-plastic-qty"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-bold text-neutral-600 uppercase">Unit Price (OMR)</label>
                              <Input
                                type="number"
                                min="0"
                                step="0.001"
                                value={openingForm.plastic_amount}
                                onChange={(e) => setOpeningForm({ ...openingForm, plastic_amount: e.target.value })}
                                placeholder="0.000"
                                data-testid="pallets-drill-opening-plastic-amount"
                              />
                            </div>
                          </div>
                        </div>
                        <div className="rounded-lg bg-white/70 border border-sky-100 p-2">
                          <div className="text-[10px] font-bold text-sky-900 uppercase tracking-wide mb-1">Wooden pallets</div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] font-bold text-neutral-600 uppercase">Qty</label>
                              <Input
                                type="number"
                                min="0"
                                value={openingForm.wooden_qty}
                                onChange={(e) => setOpeningForm({ ...openingForm, wooden_qty: e.target.value })}
                                placeholder="0"
                                data-testid="pallets-drill-opening-wooden-qty"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] font-bold text-neutral-600 uppercase">Unit Price (OMR)</label>
                              <Input
                                type="number"
                                min="0"
                                step="0.001"
                                value={openingForm.wooden_amount}
                                onChange={(e) => setOpeningForm({ ...openingForm, wooden_amount: e.target.value })}
                                placeholder="0.000"
                                data-testid="pallets-drill-opening-wooden-amount"
                              />
                            </div>
                          </div>
                        </div>
                        <Input
                          value={openingForm.notes}
                          onChange={(e) => setOpeningForm({ ...openingForm, notes: e.target.value })}
                          placeholder="Notes (optional) — e.g. 'Carried forward from paper ledger Jan 2026'"
                          data-testid="pallets-drill-opening-notes"
                        />
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" onClick={() => setOpeningForm(null)} className="rounded-full h-8 text-xs" data-testid="pallets-drill-opening-cancel">Cancel</Button>
                          <Button
                            onClick={saveOpeningBalance}
                            disabled={savingOpening}
                            className="bg-sky-700 hover:bg-sky-800 text-white rounded-full h-8 text-xs"
                            data-testid="pallets-drill-opening-save"
                          >
                            {savingOpening ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2 text-xs">
                        {/* One row per type. Both always rendered so
                            ops can see the zero if a type isn't set.
                            Feb 2026 — 4-col grid: label, qty, unit
                            price, line total (qty x unit price). */}
                        <div className="grid grid-cols-4 gap-2 rounded-md bg-white/60 px-2 py-1" data-testid="pallets-drill-opening-plastic-row">
                          <div className="text-sky-700/80 font-semibold">Plastic</div>
                          <div>
                            <span className="text-sky-700/80">Qty:</span>{' '}
                            <b className="text-sky-900" data-testid="pallets-drill-opening-plastic-qty-value">{fmtInt(detail?.opening_plastic?.opening_qty || 0)}</b>
                          </div>
                          <div>
                            <span className="text-sky-700/80">Unit Price:</span>{' '}
                            <b className="text-sky-900" data-testid="pallets-drill-opening-plastic-unit-price-value">OMR {fmt(detail?.opening_plastic?.opening_unit_price || 0)}</b>
                          </div>
                          <div>
                            <span className="text-sky-700/80">Total:</span>{' '}
                            <b className="text-sky-900" data-testid="pallets-drill-opening-plastic-amount-value">OMR {fmt(detail?.opening_plastic?.opening_amount || 0)}</b>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2 rounded-md bg-white/60 px-2 py-1" data-testid="pallets-drill-opening-wooden-row">
                          <div className="text-sky-700/80 font-semibold">Wooden</div>
                          <div>
                            <span className="text-sky-700/80">Qty:</span>{' '}
                            <b className="text-sky-900" data-testid="pallets-drill-opening-wooden-qty-value">{fmtInt(detail?.opening_wooden?.opening_qty || 0)}</b>
                          </div>
                          <div>
                            <span className="text-sky-700/80">Unit Price:</span>{' '}
                            <b className="text-sky-900" data-testid="pallets-drill-opening-wooden-unit-price-value">OMR {fmt(detail?.opening_wooden?.opening_unit_price || 0)}</b>
                          </div>
                          <div>
                            <span className="text-sky-700/80">Total:</span>{' '}
                            <b className="text-sky-900" data-testid="pallets-drill-opening-wooden-amount-value">OMR {fmt(detail?.opening_wooden?.opening_amount || 0)}</b>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2 rounded-md bg-sky-100/70 px-2 py-1 font-semibold" data-testid="pallets-drill-opening-total-row">
                          <div className="text-sky-900">Total</div>
                          <div className="text-sky-900">
                            <span className="text-sky-700/80 font-normal">Qty:</span>{' '}
                            <b>{fmtInt((detail?.opening_plastic?.opening_qty || 0) + (detail?.opening_wooden?.opening_qty || 0))}</b>
                          </div>
                          <div />
                          <div className="text-sky-900">
                            <span className="text-sky-700/80 font-normal">Total:</span>{' '}
                            <b data-testid="pallets-drill-opening-total-amount">OMR {fmt((detail?.opening_plastic?.opening_amount || 0) + (detail?.opening_wooden?.opening_amount || 0))}</b>
                          </div>
                        </div>
                        {(detail?.opening_plastic?.notes || detail?.opening_wooden?.notes) && (
                          <div className="text-sky-800/80 italic px-2">
                            {detail?.opening_plastic?.notes || detail?.opening_wooden?.notes}
                          </div>
                        )}
                        {(detail?.opening_plastic?.set_by || detail?.opening_wooden?.set_by) && (
                          <div className="text-[10px] text-sky-700/60 px-2">
                            Last set by {detail?.opening_plastic?.set_by || detail?.opening_wooden?.set_by}
                            {(detail?.opening_plastic?.set_at || detail?.opening_wooden?.set_at)
                              ? ` · ${shortDate(detail?.opening_plastic?.set_at || detail?.opening_wooden?.set_at)}`
                              : ''}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {(detail?.pending_returns || []).length > 0 && (
                    <div className="mb-4">
                      <div className="text-xs font-bold uppercase text-amber-800 mb-2">Pending approval</div>
                      <div className="space-y-2">
                        {detail.pending_returns.map((r) => (
                          <div key={r.id} className="rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid={`pallets-drill-pending-${r.id}`}>
                            <div className="flex items-center justify-between mb-2">
                              <div>
                                <div className="text-sm font-bold capitalize">{r.kind.replaceAll('_', ' ')}</div>
                                <div className="text-xs text-neutral-600">
                                  {fmtInt(r.quantity)} × {r.pallet_type}
                                  {r.amount > 0 && ` · OMR ${fmt(r.amount)}`}
                                  {r.order_reference && ` · ref ${r.order_reference}`}
                                </div>
                              </div>
                              <div className="flex gap-1">
                                {r.slip_document?.web_view_link && (
                                  <a href={r.slip_document.web_view_link} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded-full bg-white border border-neutral-200 hover:bg-neutral-50 inline-flex items-center gap-1" data-testid={`pallets-drill-slip-${r.id}`}>
                                    <ExternalLink className="w-3 h-3" /> Slip
                                  </a>
                                )}
                                <button
                                  onClick={() => rejectReturn(r.id)}
                                  disabled={confirmingId === r.id}
                                  className="text-xs px-2 py-1 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"
                                  data-testid={`pallets-drill-reject-${r.id}`}
                                >
                                  <XCircle className="w-3 h-3" /> Reject
                                </button>
                                <button
                                  onClick={() => confirmReturn(r.id)}
                                  disabled={confirmingId === r.id}
                                  className="text-xs px-2 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-40"
                                  data-testid={`pallets-drill-confirm-${r.id}`}
                                >
                                  {confirmingId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />} Confirm
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="text-xs font-bold uppercase text-neutral-600 mb-2">Transaction history</div>
                  <div className="rounded-xl border border-neutral-200 overflow-x-auto">
                    <table className="min-w-full text-xs">
                      <thead className="bg-neutral-50 border-b border-neutral-200">
                        <tr className="text-left text-[10px] uppercase text-neutral-500 font-bold">
                          <th className="p-2">Date</th>
                          <th className="p-2">Kind</th>
                          <th className="p-2 text-right">Qty</th>
                          <th className="p-2 text-right">Unit Price</th>
                          <th className="p-2 text-right">Total</th>
                          <th className="p-2">Ref</th>
                          {isSuper && <th className="p-2 text-right w-10">·</th>}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        {(detail?.transactions || []).length === 0 && (
                          <tr><td colSpan={isSuper ? 7 : 6} className="p-6 italic text-center text-neutral-500">No transactions.</td></tr>
                        )}
                        {(detail?.transactions || []).map((tx) => {
                          const absQty = Math.abs(tx.qty || 0);
                          const unitPrice = Number(tx.unit_price) > 0
                            ? Number(tx.unit_price)
                            : (absQty > 0 ? Math.abs(Number(tx.amount || 0)) / absQty : 0);
                          const isOpening = tx.kind === 'opening_balance';
                          return (
                          <tr key={tx.id} data-testid={`pallets-drill-tx-${tx.id}`} className={isOpening ? 'bg-sky-50/70' : ''}>
                            <td className="p-2">{isOpening ? (tx.ts ? shortDate(tx.ts) : '—') : shortDate(tx.ts)}</td>
                            <td className="p-2 capitalize">
                              {isOpening
                                ? <span className="inline-flex items-center gap-1 font-semibold text-sky-900"><Anchor className="w-3 h-3" /> Opening Balance{tx.pallet_type ? ` - ${tx.pallet_type.charAt(0).toUpperCase() + tx.pallet_type.slice(1)}` : ''}</span>
                                : (tx.kind || '').replaceAll('_', ' ')}
                            </td>
                            <td className={`p-2 text-right font-semibold ${tx.qty >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmtInt(absQty)}</td>
                            <td className="p-2 text-right font-mono" data-testid={`pallets-drill-tx-unit-price-${tx.id}`}>{fmt(unitPrice)}</td>
                            <td className="p-2 text-right font-mono" data-testid={`pallets-drill-tx-total-${tx.id}`}>{fmt(tx.amount)}</td>
                            <td className="p-2 text-neutral-500">{tx.order_number || tx.return_id?.slice(0, 8) || '—'}</td>
                            {isSuper && (
                              <td className="p-2 text-right">
                                {!isOpening && (
                                  <button
                                    type="button"
                                    onClick={() => deleteTx(tx)}
                                    disabled={deletingTxId === tx.id}
                                    title="Permanently delete this ledger entry"
                                    className="inline-flex items-center rounded-full border border-red-300 bg-white text-red-600 hover:bg-red-50 hover:border-red-500 text-[10px] p-1 disabled:opacity-50"
                                    data-testid={`pallets-drill-tx-delete-btn-${tx.id}`}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------
// Tab — Returns & Deposits (Phase 2)
// ---------------------------------------------------------------------

const ReturnsTab = ({ authHeaders, refresh }) => {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState('');
  // Feb 2026 — Confirm modal state. When set, opens an inline dialog
  // asking the admin to record how many pallets actually arrived (may
  // be less than the branch-submitted qty). Defaults to expected qty
  // so "received all" is a single-click flow.
  const [confirmRow, setConfirmRow] = useState(null);
  const [receivedQty, setReceivedQty] = useState('');
  const [confirmNotes, setConfirmNotes] = useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? {} : { status: filter };
      const { data } = await axios.get(`${API}/admin/pallets/returns`, { headers: authHeaders, params });
      setRows(data?.returns || []);
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || err?.message });
    } finally { setLoading(false); }
  }, [authHeaders, filter, toast]);

  useEffect(() => { load(); }, [load]);

  const openConfirm = (r) => {
    setConfirmRow(r);
    setReceivedQty(String(r.quantity || 0));
    setConfirmNotes('');
  };

  const closeConfirm = () => {
    setConfirmRow(null);
    setReceivedQty('');
    setConfirmNotes('');
  };

  const submitConfirm = async () => {
    if (!confirmRow) return;
    const parsed = parseInt(receivedQty, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
      toast({ title: 'Enter a valid received qty', description: 'Must be a positive whole number.' });
      return;
    }
    if (parsed > (confirmRow.quantity || 0)) {
      toast({ title: 'Exceeds submitted qty', description: `Max is ${confirmRow.quantity}. Use Reject if nothing arrived.` });
      return;
    }
    setBusyId(confirmRow.id);
    try {
      const body = {
        received_qty: parsed,
      };
      if (confirmNotes.trim()) body.notes = confirmNotes.trim();
      await axios.post(`${API}/admin/pallets/returns/${confirmRow.id}/confirm`, body, { headers: authHeaders });
      const shortage = (confirmRow.quantity || 0) - parsed;
      toast({
        title: 'Return confirmed',
        description: shortage > 0
          ? `Received ${parsed} of ${confirmRow.quantity} — ${shortage} short.`
          : `All ${parsed} pallets received.`,
      });
      closeConfirm();
      await load();
      await refresh();
    } catch (err) {
      toast({ title: 'Confirm failed', description: err?.response?.data?.detail || err?.message });
    } finally { setBusyId(''); }
  };

  const reject = async (id) => {
    if (!window.confirm('Reject this return? Branch will need to resubmit.')) return;
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/pallets/returns/${id}/reject`, {}, { headers: authHeaders });
      toast({ title: 'Return rejected' });
      await load();
      await refresh();
    } catch (err) {
      toast({ title: 'Reject failed', description: err?.response?.data?.detail || err?.message });
    } finally { setBusyId(''); }
  };

  return (
    <div className="space-y-4" data-testid="pallets-returns-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-neutral-900">Returns &amp; Deposits</h2>
          <p className="text-xs text-neutral-500">Approve physical pallet returns (credits warehouse inventory) or deposit settlements (reduces branch liability, no inventory change).</p>
        </div>
        <div className="flex gap-1">
          {['pending', 'confirmed', 'rejected', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full ${filter === s ? 'bg-indigo-600 text-white' : 'bg-white border border-neutral-200 text-neutral-700 hover:bg-neutral-50'}`}
              data-testid={`pallets-returns-filter-${s}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border border-neutral-200 overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-neutral-50 border-b border-neutral-200">
            <tr className="text-left text-[10px] uppercase tracking-wider text-neutral-500 font-bold">
              <th className="p-3">Date</th>
              <th className="p-3">Branch</th>
              <th className="p-3">Kind</th>
              <th className="p-3 text-right">Submitted</th>
              <th className="p-3 text-right">Received</th>
              <th className="p-3">Type</th>
              <th className="p-3 text-right">Amount</th>
              <th className="p-3">Order ref</th>
              <th className="p-3">Slip</th>
              <th className="p-3">Status</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {loading && (<tr><td colSpan={11} className="p-6 text-center text-neutral-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</td></tr>)}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={11} className="p-8 text-center italic text-neutral-500" data-testid="pallets-returns-empty">No returns in this view.</td></tr>
            )}
            {!loading && rows.map((r) => {
              const shortage = Number(r.shortage_qty) || 0;
              const hasReceived = r.received_qty !== null && r.received_qty !== undefined;
              return (
              <tr key={r.id} data-testid={`pallets-returns-row-${r.id}`}>
                <td className="p-3 text-xs">{shortDate(r.created_at)}</td>
                <td className="p-3 font-semibold">{r.branch_name}</td>
                <td className="p-3">
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${r.kind === 'physical_return' ? 'bg-indigo-50 text-indigo-800 border border-indigo-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}>
                    {r.kind === 'physical_return' ? 'Physical' : 'Deposit'}
                  </span>
                </td>
                <td className="p-3 text-right font-semibold" data-testid={`pallets-returns-submitted-${r.id}`}>{fmtInt(r.quantity)}</td>
                <td className="p-3 text-right" data-testid={`pallets-returns-received-${r.id}`}>
                  {hasReceived ? (
                    <span className="inline-flex items-center gap-1">
                      <span className={`font-semibold ${shortage > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtInt(r.received_qty)}</span>
                      {shortage > 0 && (
                        <span
                          className="text-[10px] font-bold uppercase tracking-wide bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-1.5 py-0.5"
                          data-testid={`pallets-returns-shortage-${r.id}`}
                          title={`Shortage of ${shortage} pallet${shortage === 1 ? '' : 's'}`}
                        >
                          −{fmtInt(shortage)}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className="text-neutral-400 text-xs italic">—</span>
                  )}
                </td>
                <td className="p-3 capitalize text-xs">{r.pallet_type}</td>
                <td className="p-3 text-right">{r.amount > 0 ? `OMR ${fmt(r.amount)}` : '—'}</td>
                <td className="p-3 text-xs font-mono text-neutral-600">{r.order_reference || '—'}</td>
                <td className="p-3">
                  {r.slip_document?.web_view_link ? (
                    <a href={r.slip_document.web_view_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-indigo-700 hover:underline" data-testid={`pallets-returns-slip-${r.id}`}>
                      <ExternalLink className="w-3 h-3" /> Open
                    </a>
                  ) : <span className="text-neutral-400 text-xs">—</span>}
                </td>
                <td className="p-3">
                  <span className={`inline-block text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                    r.status === 'pending' ? 'bg-amber-50 text-amber-800 border border-amber-200'
                      : r.status === 'confirmed' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                      : 'bg-neutral-100 text-neutral-700 border border-neutral-300'
                  }`}>{r.status}</span>
                </td>
                <td className="p-3">
                  {r.status === 'pending' && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => reject(r.id)}
                        disabled={busyId === r.id}
                        className="text-xs px-2 py-1 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50"
                        data-testid={`pallets-returns-reject-${r.id}`}
                      >Reject</button>
                      <button
                        onClick={() => openConfirm(r)}
                        disabled={busyId === r.id}
                        className="text-xs px-2 py-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-40"
                        data-testid={`pallets-returns-confirm-${r.id}`}
                      >
                        {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                        Confirm receipt
                      </button>
                    </div>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Feb 2026 — Confirm receipt modal. Ops enters the ACTUAL
          received qty (may be less than the branch-submitted qty).
          Amount is scaled pro-rata on the backend for deposits. */}
      {confirmRow && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="pallets-returns-confirm-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-base font-bold text-neutral-900">Confirm receipt</h3>
                <p className="text-xs text-neutral-500 mt-0.5">
                  {confirmRow.branch_name} · {confirmRow.kind === 'physical_return' ? 'Physical return' : 'Deposit settlement'} · <span className="capitalize">{confirmRow.pallet_type}</span>
                </p>
              </div>
              <button onClick={closeConfirm} className="text-neutral-400 hover:text-neutral-700" data-testid="pallets-returns-confirm-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3">
                <div className="text-[10px] uppercase font-bold text-neutral-500 tracking-wide">Submitted qty</div>
                <div className="text-2xl font-extrabold text-neutral-900 mt-1" data-testid="pallets-returns-confirm-submitted">{fmtInt(confirmRow.quantity)}</div>
                <div className="text-[10px] text-neutral-500 mt-1">By branch on {shortDate(confirmRow.created_at)}</div>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-neutral-600 tracking-wide">Received qty *</label>
                <Input
                  type="number"
                  min="1"
                  max={confirmRow.quantity}
                  value={receivedQty}
                  onChange={(e) => setReceivedQty(e.target.value)}
                  className="mt-1 font-mono text-lg font-bold text-emerald-800"
                  data-testid="pallets-returns-confirm-received-input"
                  autoFocus
                />
                {(() => {
                  const p = parseInt(receivedQty, 10);
                  if (Number.isNaN(p) || p <= 0) return <div className="text-[10px] text-neutral-400 mt-1">Enter what actually arrived.</div>;
                  const shortage = (confirmRow.quantity || 0) - p;
                  if (shortage > 0) {
                    return (
                      <div className="text-[10px] font-semibold text-amber-700 mt-1" data-testid="pallets-returns-confirm-shortage-hint">
                        Shortage: {shortage} pallet{shortage === 1 ? '' : 's'} not received.
                        {confirmRow.kind === 'deposit_settlement' && confirmRow.amount > 0
                          ? ` Deposit amount will scale to OMR ${(confirmRow.amount * p / confirmRow.quantity).toFixed(3)}.`
                          : ''}
                      </div>
                    );
                  }
                  return <div className="text-[10px] text-emerald-700 font-semibold mt-1">Full receipt — nothing short.</div>;
                })()}
              </div>
            </div>

            <div className="mb-4">
              <label className="text-[10px] uppercase font-bold text-neutral-600 tracking-wide">Notes (optional)</label>
              <Input
                value={confirmNotes}
                onChange={(e) => setConfirmNotes(e.target.value)}
                placeholder="Reason for shortage, condition, etc."
                className="mt-1"
                data-testid="pallets-returns-confirm-notes-input"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeConfirm}
                className="text-xs font-semibold px-4 py-2 rounded-full bg-white border border-neutral-200 hover:bg-neutral-50"
                data-testid="pallets-returns-confirm-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitConfirm}
                disabled={busyId === confirmRow.id}
                className="text-xs font-semibold px-4 py-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1 disabled:opacity-40"
                data-testid="pallets-returns-confirm-submit"
              >
                {busyId === confirmRow.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
                Confirm receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------
// Tab 6 — Pallet Ledger Report (PDF + Excel export) — Feb 2026
// ---------------------------------------------------------------------

const MultiPicker = ({ label, options, selected, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const base = needle
      ? options.filter((o) => (o.label || '').toLowerCase().includes(needle)
          || (o.value || '').toLowerCase().includes(needle))
      : options;
    return base.slice(0, 200);
  }, [options, q]);
  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter((v) => v !== val));
    else onChange([...selected, val]);
  };
  return (
    <div className="relative" data-testid={testId || `multi-picker-${label}`}>
      <label className="text-xs font-semibold text-neutral-600 mb-1 block">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full h-10 px-3 text-sm bg-white border border-neutral-300 rounded-md text-left flex items-center justify-between gap-2 hover:border-indigo-400"
        data-testid={`${testId || 'multi-picker'}-trigger`}
      >
        <span className="truncate">
          {selected.length === 0
            ? <span className="text-neutral-400">All {label.toLowerCase()} (none selected)</span>
            : <span className="font-semibold text-neutral-800">{selected.length} selected</span>}
        </span>
        <ChevronRight className={`w-4 h-4 text-neutral-400 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-neutral-200 rounded-md shadow-xl max-h-72 overflow-hidden flex flex-col"
             data-testid={`${testId || 'multi-picker'}-panel`}>
          <div className="p-2 border-b border-neutral-100">
            <Input
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="h-8 text-sm"
              data-testid={`${testId || 'multi-picker'}-search`}
            />
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 && (
              <div className="p-3 text-xs text-neutral-500">No matches.</div>
            )}
            {filtered.map((o) => {
              const on = selected.includes(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-t border-neutral-50 ${on ? 'bg-indigo-50 text-indigo-800' : 'hover:bg-neutral-50'}`}
                  data-testid={`${testId || 'multi-picker'}-option-${o.value}`}
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? 'bg-indigo-600 border-indigo-600 text-white' : 'border-neutral-300'}`}>
                    {on ? <Check className="w-3 h-3" /> : null}
                  </span>
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })}
          </div>
          <div className="p-2 border-t border-neutral-100 flex items-center justify-between text-xs">
            <button
              type="button"
              className="text-neutral-500 hover:text-neutral-800"
              onClick={() => onChange([])}
              data-testid={`${testId || 'multi-picker'}-clear`}
            >
              Clear
            </button>
            <button
              type="button"
              className="text-indigo-700 font-semibold"
              onClick={() => setOpen(false)}
              data-testid={`${testId || 'multi-picker'}-done`}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const LedgerReportTab = ({ ledger, authHeaders }) => {
  const { toast } = useToast();
  // Default window = this month to today.
  const today = new Date();
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  const ymd = (d) => d.toISOString().slice(0, 10);
  const [startDate, setStartDate] = useState(ymd(first));
  const [endDate, setEndDate] = useState(ymd(today));
  const [branchIds, setBranchIds] = useState([]);
  const [exportIds, setExportIds] = useState([]);
  const [localIds, setLocalIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const branchOptions = useMemo(
    () => (ledger?.branches || []).map((b) => ({
      value: b.branch_id,
      label: `${b.branch_name || b.username || b.branch_id}${b.pallet_balance ? ` · ${fmtInt(b.pallet_balance)} pallets` : ''}`,
    })),
    [ledger],
  );
  const exportOptions = useMemo(
    () => (ledger?.export_customers || []).map((e) => ({
      value: e.export_customer_id,
      label: `${e.name || e.email || e.export_customer_id}${e.country_name ? ` · ${e.country_name}` : ''}`,
    })),
    [ledger],
  );
  const localOptions = useMemo(
    () => (ledger?.local_customers || []).map((l) => ({
      value: l.local_customer_id || l.customer_number,
      label: `${l.name || l.local_customer_id || l.customer_number} · ${l.customer_number || l.local_customer_id}`,
    })),
    [ledger],
  );

  const body = useMemo(() => ({
    start_date: startDate,
    end_date: endDate,
    branch_ids: branchIds,
    export_customer_ids: exportIds,
    local_customer_ids: localIds,
  }), [startDate, endDate, branchIds, exportIds, localIds]);

  const anySelected = branchIds.length + exportIds.length + localIds.length > 0;

  const runPreview = async () => {
    if (!anySelected) {
      toast({ title: 'Pick at least one party', description: 'Select one or more branches / customers.' });
      return;
    }
    setLoadingPreview(true);
    setPreview(null);
    try {
      const res = await axios.post(
        `${API}/admin/pallets/ledger-report/preview`,
        body,
        { headers: authHeaders },
      );
      setPreview(res.data);
    } catch (err) {
      toast({ title: 'Preview failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setLoadingPreview(false);
    }
  };

  const download = async (kind) => {
    if (!anySelected) {
      toast({ title: 'Pick at least one party', description: 'Select one or more branches / customers.' });
      return;
    }
    setDownloading(kind);
    try {
      const res = await axios.post(
        `${API}/admin/pallets/ledger-report/${kind}`,
        body,
        { headers: authHeaders, responseType: 'blob' },
      );
      const ext = kind === 'pdf' ? 'pdf' : 'xlsx';
      const blob = new Blob([res.data], {
        type: kind === 'pdf'
          ? 'application/pdf'
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pallet-ledger_${startDate}_to_${endDate}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: `${kind.toUpperCase()} downloaded`, description: 'Check your downloads folder.' });
    } catch (err) {
      // Blob responses hide the JSON detail — parse if possible.
      let msg = err?.message;
      try {
        const txt = await err?.response?.data?.text?.();
        if (txt) {
          try { msg = JSON.parse(txt).detail || txt; } catch { msg = txt; }
        }
      } catch { /* noop */ }
      toast({ title: `${kind.toUpperCase()} failed`, description: msg });
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="pallet-ledger-report-tab">
      <div className="bg-white border border-neutral-200 rounded-lg p-5 space-y-5">
        <div>
          <h2 className="text-lg font-bold text-neutral-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-indigo-600" />
            Pallet Ledger Report
          </h2>
          <p className="text-sm text-neutral-500 mt-1">
            Pick a date range and the customers to include, then export a presentation-ready PDF
            or Excel with per-customer opening balance, pallets in / out and closing balance plus
            the transaction detail.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">Start date</label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="h-10"
              data-testid="pallet-ledger-report-start-date"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-neutral-600 mb-1 block">End date</label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="h-10"
              data-testid="pallet-ledger-report-end-date"
            />
          </div>
          <MultiPicker
            label="Branches"
            options={branchOptions}
            selected={branchIds}
            onChange={setBranchIds}
            testId="pallet-ledger-report-branches"
          />
          <MultiPicker
            label="Export Customers"
            options={exportOptions}
            selected={exportIds}
            onChange={setExportIds}
            testId="pallet-ledger-report-exports"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-1">
            <MultiPicker
              label="Local Customers"
              options={localOptions}
              selected={localIds}
              onChange={setLocalIds}
              testId="pallet-ledger-report-locals"
            />
          </div>
          <div className="md:col-span-3 flex flex-wrap items-end gap-2">
            <Button
              onClick={runPreview}
              disabled={loadingPreview || !anySelected}
              variant="outline"
              className="h-10"
              data-testid="pallet-ledger-report-preview-btn"
            >
              {loadingPreview
                ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                : <ClipboardList className="w-4 h-4 mr-1.5" />}
              Preview numbers
            </Button>
            <Button
              onClick={() => download('pdf')}
              disabled={!!downloading || !anySelected}
              className="h-10 bg-indigo-600 hover:bg-indigo-700"
              data-testid="pallet-ledger-report-pdf-btn"
            >
              {downloading === 'pdf'
                ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                : <FileText className="w-4 h-4 mr-1.5" />}
              Download PDF
            </Button>
            <Button
              onClick={() => download('xlsx')}
              disabled={!!downloading || !anySelected}
              className="h-10 bg-emerald-600 hover:bg-emerald-700"
              data-testid="pallet-ledger-report-xlsx-btn"
            >
              {downloading === 'xlsx'
                ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" />
                : <Upload className="w-4 h-4 mr-1.5 rotate-180" />}
              Download Excel
            </Button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="bg-white border border-neutral-200 rounded-lg p-5" data-testid="pallet-ledger-report-preview">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-neutral-800">
              Preview — {preview.range.start} → {preview.range.end}
            </h3>
            <span className="text-xs text-neutral-500">{preview.sections.length} customers</span>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-neutral-900 text-white">
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Kind</th>
                  <th className="text-right px-3 py-2">Opening</th>
                  <th className="text-right px-3 py-2">Pallets In</th>
                  <th className="text-right px-3 py-2">Pallets Out</th>
                  <th className="text-right px-3 py-2">Closing</th>
                  <th className="text-right px-3 py-2">Rows</th>
                </tr>
              </thead>
              <tbody>
                {preview.sections.map((s) => (
                  <tr key={`${s.kind}-${s.party_key}`} className="border-b border-neutral-100">
                    <td className="px-3 py-2 font-semibold">{s.display_name}</td>
                    <td className="px-3 py-2 capitalize text-neutral-500">{s.kind}</td>
                    <td className="px-3 py-2 text-right">{fmtInt(s.opening)}</td>
                    <td className="px-3 py-2 text-right text-emerald-700">{fmtInt(s.pallets_in)}</td>
                    <td className="px-3 py-2 text-right text-rose-700">{fmtInt(s.pallets_out)}</td>
                    <td className="px-3 py-2 text-right font-bold">{fmtInt(s.closing)}</td>
                    <td className="px-3 py-2 text-right text-neutral-500">{fmtInt(s.row_count)}</td>
                  </tr>
                ))}
                <tr className="bg-neutral-50 font-bold">
                  <td className="px-3 py-2">TOTAL</td>
                  <td></td>
                  <td className="px-3 py-2 text-right">{fmtInt(preview.totals.opening)}</td>
                  <td className="px-3 py-2 text-right">{fmtInt(preview.totals.in)}</td>
                  <td className="px-3 py-2 text-right">{fmtInt(preview.totals.out)}</td>
                  <td className="px-3 py-2 text-right">{fmtInt(preview.totals.closing)}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------

const TABS = [
  { key: 'pos', label: 'Purchase Orders', icon: Package2 },
  { key: 'grn', label: 'GRN', icon: ClipboardList },
  { key: 'inventory', label: 'Warehouse Inventory', icon: WarehouseIcon },
  { key: 'returns', label: 'Returns & Deposits', icon: Undo2 },
  { key: 'ledger', label: 'Branch Ledger', icon: Building2 },
  { key: 'report', label: 'Ledger Report', icon: FileText },
];

export default function AdminPallets() {
  const { authHeaders } = useAdminAuth();
  const { toast } = useToast();
  const { isSuper } = useAdminPermissions();
  const [tab, setTab] = useState('pos');
  const [pos, setPos] = useState([]);
  const [grns, setGrns] = useState([]);
  const [inventory, setInventory] = useState({ by_type: [], total_qty: 0, total_amount: 0 });
  const [transactions, setTransactions] = useState([]);
  const [ledger, setLedger] = useState({ branches: [] });
  const [loading, setLoading] = useState(true);

  const refresh = React.useCallback(async () => {
    setLoading(true);
    try {
      const [poRes, grnRes, invRes, txRes, ledRes] = await Promise.all([
        axios.get(`${API}/admin/pallets/purchase-orders`, { headers: authHeaders }),
        axios.get(`${API}/admin/pallets/grns`, { headers: authHeaders }),
        axios.get(`${API}/admin/pallets/inventory`, { headers: authHeaders }),
        axios.get(`${API}/admin/pallets/transactions`, { headers: authHeaders }),
        axios.get(`${API}/admin/pallets/ledger`, { headers: authHeaders }),
      ]);
      setPos(poRes.data?.purchase_orders || []);
      setGrns(grnRes.data?.grns || []);
      setInventory(invRes.data || { by_type: [], total_qty: 0, total_amount: 0 });
      setTransactions(txRes.data?.transactions || []);
      setLedger(ledRes.data || { branches: [] });
    } catch (err) {
      toast({ title: 'Load failed', description: err?.response?.data?.detail || err?.message });
    } finally {
      setLoading(false);
    }
  }, [authHeaders, toast]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 flex items-center gap-2" data-testid="pallets-page-title">
            <Boxes className="w-6 h-6 text-indigo-600" />
            Pallet Ledger
          </h1>
          <p className="text-sm text-neutral-500 mt-0.5">Track pallet procurement, receiving, and warehouse inventory. Phase 2 adds branch-level tracking.</p>
        </div>

        <div className="flex flex-wrap gap-1 border-b border-neutral-200" data-testid="pallets-tab-strip">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${active ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-neutral-500 hover:text-neutral-800'}`}
                data-testid={`pallets-tab-${t.key}`}
              >
                <Icon className="w-4 h-4" />
                {t.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-neutral-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading pallet data…
          </div>
        ) : (
          <>
            {tab === 'pos' && <PurchaseOrdersTab pos={pos} refresh={refresh} authHeaders={authHeaders} />}
            {tab === 'grn' && <GRNTab pos={pos} grns={grns} refresh={refresh} authHeaders={authHeaders} />}
            {tab === 'inventory' && <InventoryTab inventory={inventory} transactions={transactions} isSuper={isSuper} authHeaders={authHeaders} refresh={refresh} />}
            {tab === 'returns' && <ReturnsTab authHeaders={authHeaders} refresh={refresh} />}
            {tab === 'ledger' && <BranchLedgerTab ledger={ledger} authHeaders={authHeaders} refresh={refresh} isSuper={isSuper} />}
            {tab === 'report' && <LedgerReportTab ledger={ledger} authHeaders={authHeaders} />}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
